import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import { Firestore, collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where, deleteDoc, DocumentReference, DocumentData } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, of, firstValueFrom } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { UserStateService } from './user-state.service';
import { environment } from '../../../environments/environment';

export interface UserProfile {
  uid: string;
  email?: string;
  emailAddress?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  photoURL?: string;
  organisationId?: string | null;
  manufacturerId?: string | null;
  role?: string | null;
  createdAt?: Date;
  lastLogin?: Date;
  isAdmin?: boolean;
}

export interface Organisation {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  createdAt?: Date;
}

export interface ManufacturerOption {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private userState = inject(UserStateService);
  private injector = inject(Injector);
  
  // Collection paths with environment
  private readonly usersCollection = `environments/${environment.environmentName}/users`;
  private readonly organisationsCollection = `environments/${environment.environmentName}/organisations`;

  // Get all users (admin only)
  getAllUsers(): Observable<UserProfile[]> {
    console.log('getAllUsers called - checking admin status');
    console.log(`Using users collection path: ${this.usersCollection}`);
    
    return this.userState.isAdmin$.pipe(
      tap(isAdmin => console.log('Current admin status:', isAdmin)),
      switchMap(isAdmin => {
        if (!isAdmin) {
          console.error('Unauthorized attempt to access all users');
          return of([]);
        }
        
        console.log('Admin access confirmed, fetching all users');
        
        // Use runInInjectionContext to avoid Firebase API outside injection context error
        return from(runInInjectionContext(this.injector, () => {
          console.log('Inside injection context, getting users collection');
          return getDocs(collection(this.firestore, this.usersCollection));
        })).pipe(
          tap(snapshot => console.log(`Got snapshot with ${snapshot.docs.length} users`)),
          map(snapshot => {
            console.log(`Processing ${snapshot.docs.length} user documents`);
            const users = snapshot.docs.map(doc => {
              // Get user data
              const userData = doc.data();
              console.log(`Processing user ${doc.id}: ${JSON.stringify(userData)}`);
              
              // Create user profile with explicit isAdmin handling
              const user: UserProfile = {
                uid: doc.id,
                email: userData['email'] || '',
                emailAddress: userData['emailAddress'],
                displayName: userData['displayName'],
                firstName: userData['firstName'],
                lastName: userData['lastName'],
                photoURL: userData['photoURL'],
                organisationId: userData['organisationId'],
                manufacturerId: userData['manufacturerId'],
                role: userData['role'],
                createdAt: userData['createdAt'],
                lastLogin: userData['lastLogin'],
                isAdmin: !!userData['isAdmin'] // Ensure boolean type
              };
              
              return user;
            });
            
            console.log(`Successfully retrieved ${users.length} users`);
            return users;
          })
        );
      }),
      catchError(error => {
        console.error('Error getting all users:', error);
        console.log('Error details:', JSON.stringify(error));
        return of([]);
      })
    );
  }

  // Get users by organisation (admin or organisation member)
  getUsersByOrganisation(organisationId: string): Observable<UserProfile[]> {
    console.log(`getUsersByOrganisation called for org: ${organisationId}`);
    console.log(`Using users collection path: ${this.usersCollection}`);
    
    return this.userState.state$.pipe(
      take(1),
      switchMap(state => {
        console.log('Current user state for org users:', {
          isAdmin: state.isAdmin,
          currentUserOrgId: state.organisationId,
          targetOrgId: organisationId
        });
        
        // Check if user is admin or a member of the requested organisation
        const canAccess = state.isAdmin || state.organisationId === organisationId;
        
        if (!canAccess) {
          console.error('Unauthorized attempt to access organisation users');
          return of([]);
        }
        
        console.log('Access granted for organisation users');
        
        // Use runInInjectionContext to avoid Firebase API outside injection context error
        return from(runInInjectionContext(this.injector, () => {
          const usersQuery = query(
            collection(this.firestore, this.usersCollection),
            where('organisationId', '==', organisationId)
          );
          return getDocs(usersQuery);
        })).pipe(
          map(snapshot => {
            const users = snapshot.docs.map(doc => ({
              uid: doc.id,
              ...doc.data()
            } as UserProfile));
            console.log(`Successfully retrieved ${users.length} users for organisation ${organisationId}`);
            return users;
          })
        );
      }),
      catchError(error => {
        console.error(`Error getting users for organisation ${organisationId}:`, error);
        console.log('Error details:', JSON.stringify(error));
        return of([]);
      })
    );
  }

  // Get all organisations (admin only)
  getAllOrganisations(): Observable<Organisation[]> {
    console.log('getAllOrganisations called - checking admin status');
    console.log(`Using organisations collection path: ${this.organisationsCollection}`);
    
    return this.userState.isAdmin$.pipe(
      tap(isAdmin => console.log('Current admin status for org access:', isAdmin)),
      switchMap(isAdmin => {
        if (!isAdmin) {
          console.error('Unauthorized attempt to access all organisations');
          return of([]);
        }
        
        console.log('Admin access confirmed, fetching all organisations');
        
        // Use runInInjectionContext to avoid Firebase API outside injection context error
        return from(runInInjectionContext(this.injector, () => {
          return getDocs(collection(this.firestore, this.organisationsCollection));
        })).pipe(
          map(snapshot => {
            const orgs = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as Organisation));
            console.log(`Successfully retrieved ${orgs.length} organisations`);
            return orgs;
          })
        );
      }),
      catchError(error => {
        console.error('Error getting all organisations:', error);
        console.log('Error details:', JSON.stringify(error));
        return of([]);
      })
    );
  }

  // Create or update a user profile
  async updateUserProfile(userId: string, userData: Partial<UserProfile>): Promise<void> {
    console.log(`updateUserProfile called for user: ${userId}`);
    console.log(`Using users collection path: ${this.usersCollection}`);
    
    try {
      const state = await firstValueFrom(this.userState.state$.pipe(take(1)));
      console.log('Current user state for profile update:', {
        isAdmin: state.isAdmin,
        currentUserId: state.user?.uid,
        targetUserId: userId
      });
      
      // Check if user is admin or updating their own profile
      const canUpdate = state.isAdmin || state.user?.uid === userId;
      
      if (!canUpdate) {
        console.error('Unauthorized attempt to update user profile');
        throw new Error('Unauthorized');
      }
      
      console.log('Access granted for profile update');
      const userRef = doc(this.firestore, `${this.usersCollection}/${userId}`);
      return updateDoc(userRef, { ...userData });
    } catch (error) {
      console.error(`Error updating user ${userId}:`, error);
      console.log('Error details:', JSON.stringify(error));
      throw error;
    }
  }

  // Assign user to an organisation (admin only)
  async assignUserToOrganisation(userId: string, organisationId: string | null): Promise<void> {
    console.log(`assignUserToOrganisation called - user: ${userId}, org: ${organisationId}`);
    console.log(`Using users collection path: ${this.usersCollection}`);
    
    try {
      const isAdmin = await firstValueFrom(this.userState.isAdmin$.pipe(take(1)));
      console.log('Current admin status for assignment:', isAdmin);
      
      if (!isAdmin) {
        console.error('Unauthorized attempt to assign user to organisation');
        throw new Error('Unauthorized');
      }
      
      console.log('Admin access confirmed for user assignment');
      
      // Use runInInjectionContext to avoid Firebase API outside injection context error
      return runInInjectionContext(this.injector, async () => {
        const userRef = doc(this.firestore, `${this.usersCollection}/${userId}`);
        
        if (organisationId === null) {
          // Remove organisation assignment
          console.log(`Removing organisation assignment for user ${userId}`);
          return updateDoc(userRef, { 
            organisationId: null,
            role: null
          });
        } else {
          // Assign to organisation
          console.log(`Assigning user ${userId} to organisation ${organisationId}`);
          return updateDoc(userRef, { 
            organisationId: organisationId 
          });
        }
      });
    } catch (error) {
      console.error(`Error assigning user ${userId} to organisation:`, error);
      console.log('Error details:', JSON.stringify(error));
      throw error;
    }
  }

  // Set user role (admin only)
  async setUserRole(userId: string, role: string | null): Promise<void> {
    console.log(`setUserRole called - user: ${userId}, role: ${role}`);
    console.log(`Using users collection path: ${this.usersCollection}`);
    
    try {
      const isAdmin = await firstValueFrom(this.userState.isAdmin$.pipe(take(1)));
      console.log('Current admin status for role setting:', isAdmin);
      
      if (!isAdmin) {
        console.error('Unauthorized attempt to set user role');
        throw new Error('Unauthorized');
      }
      
      console.log('Admin access confirmed for role setting');
      
      // Use runInInjectionContext to avoid Firebase API outside injection context error
      return runInInjectionContext(this.injector, async () => {
        const userRef = doc(this.firestore, `${this.usersCollection}/${userId}`);
        return updateDoc(userRef, { role });
      });
    } catch (error) {
      console.error(`Error setting role for user ${userId}:`, error);
      console.log('Error details:', JSON.stringify(error));
      throw error;
    }
  }

  // Create a new organisation (admin only)
  async createOrganisation(organisation: Omit<Organisation, 'id'>): Promise<string> {
    console.log('createOrganisation called');
    console.log(`Using organisations collection path: ${this.organisationsCollection}`);
    
    try {
      const isAdmin = await firstValueFrom(this.userState.isAdmin$.pipe(take(1)));
      console.log('Current admin status for org creation:', isAdmin);
      
      if (!isAdmin) {
        console.error('Unauthorized attempt to create organisation');
        throw new Error('Unauthorized');
      }
      
      console.log('Admin access confirmed for org creation');
      
      // Use runInInjectionContext to avoid Firebase API outside injection context error
      return runInInjectionContext(this.injector, async () => {
        const orgRef = doc(collection(this.firestore, this.organisationsCollection));
        const newOrg = {
          ...organisation,
          createdAt: new Date()
        };
        
        await setDoc(orgRef, newOrg);
        console.log(`Organisation created with ID: ${orgRef.id}`);
        return orgRef.id;
      });
    } catch (error) {
      console.error('Error creating organisation:', error);
      console.log('Error details:', JSON.stringify(error));
      throw error;
    }
  }

  // Update an organisation (admin only)
  async updateOrganisation(id: string, data: Partial<Organisation>): Promise<void> {
    console.log(`updateOrganisation called for org: ${id}`);
    console.log(`Using organisations collection path: ${this.organisationsCollection}`);
    
    try {
      const isAdmin = await firstValueFrom(this.userState.isAdmin$.pipe(take(1)));
      console.log('Current admin status for org update:', isAdmin);
      
      if (!isAdmin) {
        console.error('Unauthorized attempt to update organisation');
        throw new Error('Unauthorized');
      }
      
      console.log('Admin access confirmed for org update');
      
      // Use runInInjectionContext to avoid Firebase API outside injection context error
      return runInInjectionContext(this.injector, async () => {
        const orgRef = doc(this.firestore, `${this.organisationsCollection}/${id}`);
        return updateDoc(orgRef, { ...data });
      });
    } catch (error) {
      console.error(`Error updating organisation ${id}:`, error);
      console.log('Error details:', JSON.stringify(error));
      throw error;
    }
  }

  // Delete an organisation (admin only)
  async deleteOrganisation(id: string): Promise<void> {
    console.log(`deleteOrganisation called for org: ${id}`);
    console.log(`Using collections paths - organisations: ${this.organisationsCollection}, users: ${this.usersCollection}`);
    
    try {
      const isAdmin = await firstValueFrom(this.userState.isAdmin$.pipe(take(1)));
      console.log('Current admin status for org deletion:', isAdmin);
      
      if (!isAdmin) {
        console.error('Unauthorized attempt to delete organisation');
        throw new Error('Unauthorized');
      }
      
      console.log('Admin access confirmed for org deletion');
      
      // Use runInInjectionContext to avoid Firebase API outside injection context error
      return runInInjectionContext(this.injector, async () => {
        // First, get all users in this organisation
        const usersQuery = query(
          collection(this.firestore, this.usersCollection),
          where('organisationId', '==', id)
        );
        
        const snapshot = await getDocs(usersQuery);
        console.log(`Found ${snapshot.docs.length} users to update for org deletion`);
        
        // Update all users to remove organisation association
        const userUpdates = snapshot.docs.map(doc => {
          const userRef = doc.ref;
          return updateDoc(userRef, { 
            organisationId: null,
            role: null
          });
        });
        
        // Wait for all user updates to complete
        await Promise.all(userUpdates);
        console.log(`Updated ${userUpdates.length} users to remove organisation association`);
        
        // Delete the organisation
        const orgRef = doc(this.firestore, `${this.organisationsCollection}/${id}`);
        await deleteDoc(orgRef);
        console.log(`Organisation ${id} deleted successfully`);
      });
    } catch (error) {
      console.error(`Error deleting organisation ${id}:`, error);
      console.log('Error details:', JSON.stringify(error));
      throw error;
    }
  }

  // Set admin claim for a user (requires Cloud Function)
  async setAdminClaim(userId: string, isAdmin: boolean): Promise<void> {
    console.log(`setAdminClaim called for user: ${userId}, isAdmin: ${isAdmin}`);
    
    try {
      const currentUserIsAdmin = await firstValueFrom(this.userState.isAdmin$.pipe(take(1)));
      console.log('Current admin status for setting admin claim:', currentUserIsAdmin);
      
      if (!currentUserIsAdmin) {
        console.error('Unauthorized attempt to set admin claim');
        throw new Error('Unauthorized');
      }
      
      console.log('Admin access confirmed, calling cloud function');
      const setAdminRole = httpsCallable(this.functions, 'setAdminRole');
      await setAdminRole({ userId, isAdmin });
      console.log('Cloud function called successfully');
      
      // Also update the user document in Firestore to reflect the admin status
      // This helps with UI rendering even if the token hasn't been refreshed yet
      const userRef = doc(this.firestore, `${this.usersCollection}/${userId}`);
      await updateDoc(userRef, { isAdmin });
      console.log('User document updated with isAdmin:', isAdmin);
      
      // After setting the claim, refresh the token if this is the current user
      const currentState = await firstValueFrom(this.userState.state$.pipe(take(1)));
      if (userId === currentState.user?.uid) {
        console.log('Setting admin claim for current user, refreshing state');
        return this.userState.refreshUserState();
      }
    } catch (error) {
      console.error(`Error setting admin claim for user ${userId}:`, error);
      console.log('Error details:', JSON.stringify(error));
      throw error;
    }
  }

  // Update user organisation / manufacturer / role in one write (admin only).
  // Contributor ↔ organisationId; manufacturer ↔ manufacturerId; mutually exclusive.
  async updateUserAssignment(
    userId: string,
    assignment: {
      role: string | null;
      organisationId?: string | null;
      manufacturerId?: string | null;
    },
  ): Promise<void> {
    const { role } = assignment;
    let organisationId =
      assignment.organisationId === undefined
        ? null
        : assignment.organisationId;
    let manufacturerId =
      assignment.manufacturerId === undefined
        ? null
        : assignment.manufacturerId;

    if (role === 'contributor') {
      manufacturerId = null;
    } else if (role === 'manufacturer') {
      organisationId = null;
    } else {
      organisationId = null;
      manufacturerId = null;
    }

    console.log(
      `updateUserAssignment - user: ${userId}, role: ${role}, org: ${organisationId}, mfr: ${manufacturerId}`,
    );

    try {
      const isAdmin = await firstValueFrom(this.userState.isAdmin$.pipe(take(1)));
      if (!isAdmin) {
        throw new Error('Unauthorized');
      }

      return runInInjectionContext(this.injector, async () => {
        const userRef = doc(this.firestore, `${this.usersCollection}/${userId}`);
        return updateDoc(userRef, {
          organisationId,
          manufacturerId,
          role: role || null,
        });
      });
    } catch (error) {
      console.error(`Error updating assignment for user ${userId}:`, error);
      throw error;
    }
  }
} 